import React, { useState } from "react";
import { UserPlus } from "lucide-react";
import { affiliateAPI } from "../../services/AdminPortal/affiliateAPI";

const AddAffiliatePartnerModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    partnerName: "",
    emailId: "",
    phoneNumber: "",
    whatsappNumber: "",
    sameAsPhone: false,
    district: "",
    state: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "sameAsPhone" && checked) {
      setFormData((prev) => ({ ...prev, whatsappNumber: prev.phoneNumber }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Prepare the data for API
      const networkData = {
        name: formData.partnerName,
        email: formData.emailId,
        phone: formData.phoneNumber,
        whatsapp: formData.whatsappNumber,
        district: formData.district,
        state: formData.state,
        status: "pending", // Default status for new partners
      };
      const response = await affiliateAPI.createNetwork(networkData);

      if (response.success) {
        onAdd(response.data); 
        onClose();
        setFormData({
          partnerName: "",
          emailId: "",
          phoneNumber: "",
          whatsappNumber: "",
          sameAsPhone: false,
          district: "",
          state: "",
        });
      } else {
        throw new Error(
          response.message || "Failed to create network"
        );
      }
    } catch (error) {
      console.error("Error creating network:", error);
      setError(
        error.message || "Failed to create network. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#1e1e1e] p-6 rounded-lg w-full h-full max-h-[400px] max-w-3xl">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 rounded-full bg-[#FFCA20] text-black flex items-center justify-center mr-2">
            <UserPlus />
          </div>
          <h2 className="text-lg font-bold text-white">
            Add Affiliate Partner
          </h2>
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500 rounded text-red-500 text-sm">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Partner Name
              </label>
              <input
                type="text"
                name="partnerName"
                value={formData.partnerName}
                onChange={handleChange}
                className="w-full bg-[#252525] border border-[#333333] rounded p-2 text-white focus:outline-none"
                placeholder="Enter Affiliate Partner Name"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Email ID
              </label>
              <input
                type="email"
                name="emailId"
                value={formData.emailId}
                onChange={handleChange}
                className="w-full bg-[#252525] border border-[#333333] rounded p-2 text-white focus:outline-none"
                placeholder="Enter Partner Email ID"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className="w-full bg-[#252525] border border-[#333333] rounded p-2 text-white focus:outline-none"
                placeholder="Enter Partner Phone Number"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Whatsapp Number
              </label>
              <div className="relative">
                <input
                  type="tel"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  className="w-full bg-[#252525] border border-[#333333] rounded p-2 text-white focus:outline-none"
                  placeholder="Enter Partner Whatsapp Number"
                  required
                />
                <div className="absolute right-2 top-1/2 transform mt-[-2em] -translate-y-1/2">
                  <label className="inline-flex items-center text-sm text-gray-400">
                    <input
                      type="checkbox"
                      name="sameAsPhone"
                      checked={formData.sameAsPhone}
                      onChange={handleChange}
                      className="form-checkbox h-4 w-4 text-[#FFCA20] rounded border-[#333333] focus:ring-yellow-500"
                    />
                    <span className="ml-2">Same as Phone Number</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                District
              </label>
              <select
                name="district"
                value={formData.district}
                onChange={handleChange}
                className="w-full bg-[#252525] border border-[#333333] rounded p-2 text-white focus:outline-none appearance-none"
              >
                <option value="">Select District</option>
                {/* Add your district options here */}
                <option value="hyderabad">Hyderabad</option>
                <option value="rangareddy">Rangareddy</option>
                {/* ... more districts ... */}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">State</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full bg-[#252525] border border-[#333333] rounded p-2 text-white focus:outline-none appearance-none"
              >
                <option value="">Select State</option>
                {/* Add your state options here */}
                <option value="telangana">Telangana</option>
                <option value="andhra-pradesh">Andhra Pradesh</option>
                {/* ... more states ... */}
              </select>
            </div>
          </div>
          <div className="flex justify-between gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-[#252525] border border-[#333333] rounded p-2 text-white focus:outline-none appearance-none"
              disabled={isLoading}
            >
              Go Back
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-500 text-white rounded focus:outline-none flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  Confirm & Send to Admin
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 ml-2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAffiliatePartnerModal;
